import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Images } from "lucide-react";
import { fmtRange, fmtTime, fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { SIZES } from "@/components/shared/Img";
import { EventImage } from "@/components/shared/EventImage";
import { displayFor } from "@/lib/display";
import { CapacityMeter } from "@/components/events/CapacityMeter";

/** «+3» on a thumbnail when the event has more photos than the cover. */
const MoreBadge = ({ event, className }) => {
  const extra = (event.images?.length || 0) - 1;
  if (extra < 1) return null;
  return <span className={cn("pointer-events-none absolute inline-flex items-center gap-1 rounded-full bg-ink/70 px-1.5 py-0.5 text-[10.5px] font-medium tabular text-paper backdrop-blur", className)} data-testid={`event-more-images-${event.id}`}><Images className="h-3 w-3" /> +{extra}</span>;
};

/** The same count, written beside the picture when it is shown whole (nothing may lie on a poster). */
const MoreInline = ({ event, light = false }) => {
  const extra = (event.images?.length || 0) - 1;
  if (extra < 1) return null;
  return <span className={cn("inline-flex items-center gap-1 tabular", light ? "text-paper/55" : "text-muted-foreground")} data-testid={`event-more-images-${event.id}`}><Images className="h-3 w-3" /> +{extra} bilete</span>;
};

/** Listing thumbnail: fixed height, width from the picture's own ratio when it is shown whole (unknown size → the usual 112×80). */
const RowThumb = ({ event }) => {
  const d = displayFor(event, event.image);
  const whole = d.fit === "contain";
  const w = whole ? Math.round(Math.min(128, Math.max(56, 80 * d.ratio))) : 112;
  return <EventImage src={event.image} display={{ ...d, ratio: w / 80 }} measure={false} alt="" width={240} widths={[240, 480]} sizes={`${w}px`} className="h-20 rounded-[8px]" imgClassName="rounded-[8px]" testId={`event-row-thumb-${event.id}`} />;
};

/** Small inline ticket status for programme lists: dot + text, sol dot when urgent. */
export const TicketTag = ({ t, light = false }) => {
  if (!t) return null;
  let text = t.free ? "Påmelding" : "Billettar";
  let tone = light ? "text-paper/80" : "text-foreground/80";
  let dot = light ? "bg-paper/50" : "bg-foreground/40";
  const doorOnly = t.door_sales && !t.fully_sold_out && (!t.sales_open || t.sold_out);
  if (doorOnly) { text = t.sales_open ? "Utselt på nett · dørsal" : "Billettar i døra"; tone = light ? "text-paper/80" : "text-foreground/80"; dot = light ? "bg-paper/50" : "bg-foreground/40"; }
  else if (!t.sales_open) { text = "Salet stengt"; tone = light ? "text-paper/55" : "text-muted-foreground"; dot = light ? "bg-paper/30" : "bg-border"; }
  else if (t.sold_out) { text = t.waitlist_enabled ? "Utseld · venteliste" : "Utseld"; tone = light ? "text-paper/70" : "text-muted-foreground"; dot = light ? "bg-paper/30" : "bg-border"; }
  else if (t.few_left) { text = t.available != null ? `Få att – ${t.available}` : "Få att"; tone = "text-sol"; dot = "bg-sol"; }
  return <span className={cn("inline-flex items-center gap-2 font-medium tabular", tone)} data-testid="event-ticket-tag"><span className={cn("inline-block h-1.5 w-1.5 rounded-full", dot)} aria-hidden="true" />{text}</span>;
};

/** Programme row: big day number, title, meta, thumbnail. `light` for dark backgrounds. */
export const EventRow = ({ event, className, light = false }) => (
  <Link
    to={`/arrangement/${event.id}`}
    className={cn(
      "group -mx-3 grid grid-cols-[64px_1fr] items-center gap-4 rounded-[12px] px-3 py-4 transition-colors sm:-mx-4 sm:px-4 sm:py-5 md:grid-cols-[88px_1fr_auto] md:gap-8",
      light ? "hover:bg-paper/[0.06]" : "hover:bg-card",
      className
    )}
    data-testid={`event-card-${event.id}`}
  >
    <div className="text-center">
      <div className={cn("font-display text-[32px] font-semibold leading-none tracking-[-0.04em] tabular sm:text-[40px]", light ? "text-paper" : "text-foreground")}>{fmtDate(event.date, "d")}</div>
      <div className={cn("t-mono mt-1", light ? "text-paper/55" : "text-muted-foreground")}>{fmtDate(event.date, "MMM").replace(".", "")}</div>
    </div>
    <div className="min-w-0">
      {event.category && <div className={cn("t-mono", light ? "text-sol" : "text-muted-foreground")}>{event.category}</div>}
      <h3 className={cn("t-h3 mt-1.5 text-balance u-hover", light && "text-paper")}>{event.title}</h3>
      <div className={cn("mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[14px]", light ? "text-paper/65" : "text-muted-foreground")}>
        <span>{event.end_date && event.end_date !== event.date ? fmtRange(event.date, event.end_date) : fmtDate(event.date, "EEEE")}</span>
        {(event.start_time || event.end_time) && <span>{fmtTime(event.start_time, event.end_time)}</span>}
        {event.location && <span>{event.location}</span>}
        {event.price_text && <span className={cn("tabular", light ? "text-paper" : "text-foreground")}>{event.price_text}</span>}
        {event.tickets && <TicketTag t={event.tickets} light={light} />}
      </div>
      {event.tickets?.capacity != null && event.tickets.available != null && !event.tickets.sold_out && (event.tickets.capacity - event.tickets.available) / event.tickets.capacity >= 0.6 && (
        <CapacityMeter compact available={event.tickets.available} capacity={event.tickets.capacity} fewLeft={event.tickets.few_left} className="mt-2 max-w-[220px]" testId={`event-row-meter-${event.id}`} />
      )}
    </div>
    <div className="hidden items-center gap-5 md:flex">
      {event.image && <span className="relative flex items-center gap-2"><RowThumb event={event} />{displayFor(event, event.image).fit === "contain" ? null : <MoreBadge event={event} className="bottom-1.5 right-1.5" />}</span>}
      <span className={cn("flex h-10 w-10 items-center justify-center rounded-full transition-colors", light ? "bg-paper/10 group-hover:bg-sol group-hover:text-ink" : "bg-secondary group-hover:bg-foreground group-hover:text-background")}><ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" /></span>
    </div>
  </Link>
);

/** Image-on-top tile for grids. A whole poster gets at most a 3:4 frame here (soft bands), so rows stay even. */
export const EventCard = ({ event }) => {
  const [resolved, setResolved] = useState(null);
  const d = event.image ? resolved || displayFor(event, event.image, { minRatio: 3 / 4 }) : null;
  const whole = d?.fit === "contain";
  return (
    <Link to={`/arrangement/${event.id}`} className="group block" data-testid={`event-card-${event.id}`}>
      <figure className="relative overflow-hidden rounded-[12px] bg-secondary">
        {event.image ? (
          <EventImage src={event.image} display={d} onResolve={setResolved} alt={event.title} width={800} sizes={SIZES.third} hover className="rounded-[12px]" imgClassName="rounded-[12px]" testId={`event-card-image-${event.id}`}>
            {!whole && <MoreBadge event={event} className="bottom-3 right-3 px-2 py-1 text-[11.5px]" />}
          </EventImage>
        ) : (
          <div className="flex aspect-[4/3] w-full items-end p-5" aria-hidden="true">
            <span className="font-display text-[64px] font-semibold leading-none tracking-[-0.04em] tabular text-foreground/80">{fmtDate(event.date, "d")}<span className="ml-1 text-[18px] tracking-normal text-muted-foreground">{fmtDate(event.date, "MMM").replace(".", "")}</span></span>
          </div>
        )}
      </figure>
      <div className="mt-4 flex items-start justify-between gap-4">
        <div>
          <div className="t-mono flex flex-wrap items-center gap-x-2 text-muted-foreground"><span>{fmtRange(event.date, event.end_date)}{event.category ? ` · ${event.category}` : ""}</span>{whole && <MoreInline event={event} />}</div>
          <h3 className="t-h3 mt-2 text-balance u-hover">{event.title}</h3>
        </div>
        <ArrowUpRight className="mt-1 h-5 w-5 shrink-0 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </div>
    </Link>
  );
};
